<script lang="ts" setup>
import type { CommandPaletteGroup, CommandPaletteItem, InputMenuItem } from '@nuxt/ui'
import { format } from 'date-fns'
import type { Locale } from 'date-fns'
import { es, fr, it, de } from 'date-fns/locale'
import { refDebounced } from '@vueuse/core'
import { today, getLocalTimeZone } from '@internationalized/date'
import type { CalendarDate, DateValue } from '@internationalized/date'

// Currency Store
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)

// Idioma
const { locale, t } = useI18n()
const { trackSearchLocationHistoryClicked, trackSearchDateRangeModeClicked } = usePosthogFunnel()

// Loading (navegación / cambio de idioma)
const { isLoading } = useLoadingIndicator()
const showSkeletonTransition = ref(false)

// Mostrar skeleton al cambiar idioma o moneda (evita flash antes de que isLoading sea true)
watch([locale, currency], () => {
  showSkeletonTransition.value = true
  // Failsafe: si isLoading no se activa pronto, reseteamos para no quedar oculto
  setTimeout(() => {
    if (!isLoading.value) {
      showSkeletonTransition.value = false
    }
  }, 500)
}, { immediate: false })

watch(isLoading, (loading) => {
  if (!loading) showSkeletonTransition.value = false
}, { immediate: true })

const showHeaderSkeleton = computed(() => isLoading.value || showSkeletonTransition.value)

// Ubicacion
const selectedDestStore = useDestinationStore()
const { selectedDestination, labelDestination, labelDestinationDisplay, destinationDisplayMode, cookieDestination, hotelDispo } = storeToRefs(selectedDestStore)

// Evitar hydration mismatch: el icono del input (map-pin vs loader-circle) solo
// refleja loading después de montar, así servidor y cliente renderizan igual.
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

// Fecha
const selectedDiaStore = useSelectedDayStore()
const { selectedDay } = storeToRefs(selectedDiaStore)

watch(selectedDay, () => {
  isOpenSearch.value = false
  openCalendar.value = false
  openCalendarPopover.value = false
  if (mobileButton.value) {
    buscar()
  }
})

// CalendarDate conversion for UCalendar
const calendarDate = computed({
  get: (): DateValue => {
    const cd = selectedDay.value ? dateToCalendarDate(selectedDay.value) : null
    return cd || today(getLocalTimeZone())
  },
  set: (value: DateValue | null) => {
    if (value && 'year' in value) {
      const date = calendarDateToDate(value as CalendarDate)
      if (date) {
        selectedDiaStore.setDay(date)
      }
    }
  }
})

// Min date for calendar (today)
const minCalendarDate = computed<DateValue>(() => today(getLocalTimeZone()))

const selectHoy = () => {
  calendarDate.value = today(getLocalTimeZone())
  openCalendar.value = false
  openCalendarPopover.value = false
}
const selectMañana = () => {
  calendarDate.value = today(getLocalTimeZone()).add({ days: 1 })
  openCalendar.value = false
  openCalendarPopover.value = false
}

function dayOffsetFromSelected(day: DateValue): number | null {
  if (!selectedDay.value) return null
  const sel = dateToCalendarDate(selectedDay.value)
  if (!sel) return null
  return day.compare(sel)
}

function isDayAfterSelected(day: DateValue): boolean {
  return dayOffsetFromSelected(day) === 1
}

function isDayBeforeSelected(day: DateValue): boolean {
  if (dayOffsetFromSelected(day) !== -1) return false
  return day.compare(today(getLocalTimeZone())) >= 0
}

const canSelectDayBefore = computed(() => {
  if (!selectedDay.value) return false
  const sel = dateToCalendarDate(selectedDay.value)
  if (!sel) return false
  return sel.compare(today(getLocalTimeZone())) > 0
})

const locales: Record<string, Locale> = { es, fr, it, de }
const formatDatePicker = (date: Date | string | number | null) => {
  if (date == null) return ''
  date = new Date(date)
  const day = format(date, 'dd', { locale: locales[locale.value] })
  const month = format(date, 'MMMM', { locale: locales[locale.value] })
  if (locale.value === 'es') {
    return `${day} de ${month}`
  } else if (locale.value === 'it' || locale.value === 'fr' || locale.value === 'de') {
    return `${day} ${month}`
  } else {
    return `${month} ${day}`
  }
}

// Ubicacion
const searchTerm = ref('')
const searchTermDebounced = refDebounced(searchTerm, 200)
function syncLabelDestinoInput() {
  if (editingDestInput.value) return
  const d = selectedDestination.value
  if (d == null) {
    labelDestino.value = undefined
    return
  }
  const mode = destinationDisplayMode.value
  if (mode === 'gps_near_me') {
    labelDestino.value = destinationToInputMenuItem({
      ...d,
      name_translated: t('labels.ubicacionActual'),
      label: t('labels.ubicacionActual')
    } as Destination)
  } else if (mode === 'map_point') {
    labelDestino.value = destinationToInputMenuItem({
      ...d,
      name_translated: t('labels.zonaActual'),
      label: t('labels.zonaActual')
    } as Destination)
  } else {
    labelDestino.value = destinationToInputMenuItem(d)
  }
}

const labelDestino = ref<InputMenuItem | undefined>(undefined)
/** Evita que el watcher vuelva a rellenar el input mientras el usuario elige otra ubicación. */
const editingDestInput = ref(false)

watch(selectedDestination, syncLabelDestinoInput, { deep: true, immediate: true })
watch(destinationDisplayMode, () => {
  if (!editingDestInput.value) syncLabelDestinoInput()
})
watch(locale, () => {
  if (!editingDestInput.value) syncLabelDestinoInput()
})
// Search Modals Store
const searchModalsStore = useSearchModalsStore()
const { openDest, openCalendar, dateRangeMode } = storeToRefs(searchModalsStore)

const openCalendarPopover = ref(false)
const openDestInput = ref(false)
const mobileButton = ref(false)
const destHeaderInputMenu = useTemplateRef<{ inputRef: HTMLInputElement | null }>('destHeaderInputMenu')

const { destinations, pendingDest, refresh: refreshAutocomplete } =
  useDestinationAutocompleteQuery(searchTermDebounced)

watch(openDest, (isOpen) => {
  if (isOpen) searchTerm.value = ''
})

watch(openDestInput, (isOpen) => {
  if (isOpen) {
    clearInput()
  } else if (editingDestInput.value) {
    editingDestInput.value = false
    syncLabelDestinoInput()
  }
})

const isDestinationAutocompleteSearch = computed(
  () => searchTerm.value.trim().length > 2
)

watch([openDest, openDestInput, searchTermDebounced, locale], () => {
  const term = searchTermDebounced.value.trim()
  const modalWantsApi = openDest.value && term.length > 2
  const inputWantsApi = openDestInput.value && term.length > 2
  if (!modalWantsApi && !inputWantsApi) return
  void refreshAutocomplete()
})

const route = useRoute()
const router = useRouter()

const dateRangeModeOptions = computed(() => [
  { value: 'minus1' as const, label: t('labels.dateRangeShortMinus1'), disabled: !canSelectDayBefore.value, ariaLabel: t('labels.dateRangeMinus1') },
  { value: 'plus1' as const, label: t('labels.dateRangeShortPlus1'), disabled: false, ariaLabel: t('labels.dateRangePlus1') },
  { value: 'both' as const, label: t('labels.dateRangeShortBoth'), disabled: !canSelectDayBefore.value, ariaLabel: t('labels.dateRangeBoth') },
])

const dateRangeModeShortLabel = computed(() => {
  const mode = dateRangeMode.value
  if (mode === 'minus1') return '- 1'
  if (mode === 'plus1') return '+ 1'
  if (mode === 'both') return '± 1'
  return ''
})

const calendarDateMainLabel = computed(() => formatDatePicker(selectedDay.value))

const calendarDateAriaLabel = computed(() => {
  const main = calendarDateMainLabel.value
  const mode = dateRangeMode.value
  if (mode === 'minus1') return `${main}, ${t('labels.dateRangeShortMinus1')}`
  if (mode === 'plus1') return `${main}, ${t('labels.dateRangeShortPlus1')}`
  if (mode === 'both') return `${main}, ${t('labels.dateRangeShortBoth')}`
  return main
})

function selectDateRangeMode(mode: Exclude<DateRangeMode, ''>) {
  if ((mode === 'minus1' || mode === 'both') && !canSelectDayBefore.value) return
  const previous = dateRangeMode.value
  const next: DateRangeMode = previous === mode ? '' : mode
  trackSearchDateRangeModeClicked({
    mode,
    action: next ? 'selected' : 'deselected',
    previous_mode: previous || undefined,
    new_mode: next || undefined,
    search_date: selectedDay.value ? formatDateGuion(selectedDay.value) : undefined,
    destination_id: selectedDestination.value?.element_id ?? selectedDestination.value?.id ?? undefined,
    locale: locale.value,
    is_mobile: isMobile,
    surface: openCalendar.value ? 'calendar_modal' : 'calendar_popover',
  })
  searchModalsStore.setDateRangeMode(next)
}

function isRangeDayMarked(day: DateValue): boolean {
  if (dateRangeIncludesAfter(dateRangeMode.value) && isDayAfterSelected(day)) return true
  if (dateRangeIncludesBefore(dateRangeMode.value) && isDayBeforeSelected(day)) return true
  return false
}

watch(() => route.query['data-dateRangeMode'], () => {
  if (route.path.includes('search')) {
    searchModalsStore.syncDateRangeModeFromQuery(route.query)
  }
}, { immediate: true })
watch(() => route.query['data-dateRange'], () => {
  if (!route.path.includes('search') || route.query['data-dateRangeMode'] != null) return
  searchModalsStore.syncDateRangeModeFromQuery(route.query)
})
watch(canSelectDayBefore, (can) => {
  if (!can && dateRangeMode.value === 'minus1') searchModalsStore.setDateRangeMode('')
  if (!can && dateRangeMode.value === 'both') searchModalsStore.setDateRangeMode('plus1')
})

function syncDateRangeModeToSearchRoute(mode: DateRangeMode) {
  if (!route.path.includes('search')) return
  if (parseDateRangeModeFromQuery(route.query) === mode) return
  const nextQuery = { ...route.query }
  if (mode) nextQuery['data-dateRangeMode'] = mode
  else delete nextQuery['data-dateRangeMode']
  delete nextQuery['data-dateRange']
  delete nextQuery['data-dateRangeBefore']
  delete nextQuery['data-dateRangeAfter']
  void router.replace({ query: nextQuery })
}

watch(dateRangeMode, (mode) => {
  syncDateRangeModeToSearchRoute(mode)
})

// Preservar scroll al cerrar el modal del calendario (evita que el footer suba en desktop)
// En la página de búsqueda no restauramos para que al cambiar ubicación y cerrar el calendario se mantenga el scroll arriba
const scrollPositionWhenCalendarOpened = ref<{ x: number; y: number } | null>(null)
watch(openCalendar, (isOpen) => {
  if (isOpen) {
    scrollPositionWhenCalendarOpened.value = { x: window.scrollX, y: window.scrollY }
  } else if (scrollPositionWhenCalendarOpened.value !== null) {
    const { x, y } = scrollPositionWhenCalendarOpened.value
    scrollPositionWhenCalendarOpened.value = null
    const isSearchPage = route.path.includes('search')
    if (!isSearchPage) {
      // Restaurar después de que el modal haya cerrado (animación + liberación del body)
      setTimeout(() => {
        window.scrollTo(x, y)
      }, 100)
    }
  }
})

// Ubicacion Actual
const labelUbicacionActual = t('labels.ubicacionActual')
const destinoActualLocation = ref('')
const destinoActual = computed(() => {
  return [
    {
      id: null,
      label: labelUbicacionActual,
      name_translated: labelUbicacionActual,
      location: destinoActualLocation.value,
      icon: 'i-ic-round-near-me',
      locationPickKind: 'current_gps' as const,
    }
  ]
})

const groupUbicacion = () => ({
  id: 'actions',
  items: destinoActual.value,
  ignoreFilter: true,
})

const { locations: searchLocationHistory, pushLocation } = useSearchLocationHistory()

const defaultDestination = ref([
  {
    id: 'top-destinations',
    label: t('labels.destinosPopulares'),
    items: defaultDestinations[locale.value] || [],
  }
])

const commandPaletteGroups = computed((): CommandPaletteGroup<CommandPaletteItem>[] => {
  const blocks: CommandPaletteGroup<CommandPaletteItem>[] = []

  if (!isDestinationAutocompleteSearch.value) {
    blocks.push(groupUbicacion() as CommandPaletteGroup<CommandPaletteItem>)
    if (searchLocationHistory.value.length) {
      blocks.push({
        id: 'search-history',
        label: t('labels.busquedasRecientes'),
        items: searchLocationHistory.value.map(item => ({ ...item, fromSearchHistory: true })) as CommandPaletteItem[],
        ignoreFilter: true,
      })
    }
    blocks.push({
      id: 'default-destinations',
      label: t('labels.destinosPopulares'),
      items: (defaultDestination.value[0]?.items || []) as CommandPaletteItem[],
      ignoreFilter: true,
    })
  } else if (destinations.value?.data?.length) {
    blocks.push(groupUbicacion() as CommandPaletteGroup<CommandPaletteItem>)
    blocks.push({
      id: 'autocomplete',
      label: `${t('labels.searchDestino')} “${searchTerm.value}”...`,
      items: (destinations.value?.data || []) as CommandPaletteItem[],
      ignoreFilter: true,
    })
  }

  return blocks
})

// Lista para UInputMenu: array plano con type label / separator (docs InputMenu)
const itemsInput = computed((): InputMenuItem[] => {
  const pushSeparator = (out: InputMenuItem[]) => {
    if (out.length) out.push({ type: 'separator' })
  }

  if (!isDestinationAutocompleteSearch.value) {
    const out: InputMenuItem[] = [...destinoActual.value as InputMenuItem[]]
    if (searchLocationHistory.value.length) {
      pushSeparator(out)
      out.push(
        { type: 'label', label: t('labels.busquedasRecientes') },
        ...searchLocationHistory.value.map(item => ({
          ...item,
          fromSearchHistory: true,
        })) as InputMenuItem[],
      )
    }
    const popular = defaultDestinations[locale.value] || []
    if (popular.length) {
      pushSeparator(out)
      out.push(
        { type: 'label', label: t('labels.destinosPopulares') },
        ...(popular as InputMenuItem[]),
      )
    }
    return out
  }

  if (pendingDest.value === 'pending') {
    return []
  }

  const apiRows = destinations.value?.data ?? []
  if (!apiRows.length) {
    return []
  }

  const out: InputMenuItem[] = [...destinoActual.value as InputMenuItem[]]
  pushSeparator(out)
  out.push(
    { type: 'label', label: `${t('labels.searchDestino')} “${searchTerm.value}”...` },
    ...(apiRows as InputMenuItem[]),
  )
  return out
})

const toast = useToast()

function saveLocationSearch(destination: Destination | null | undefined) {
  pushLocation(destination)
}

/** Tras el GPS: resuelve ciudad/destino vía API `/destination` (como GeoIP); si falla, mantiene el destino sintético. */
async function finishCurrentLocationSelection(
  coordsStr: string,
  fallbackDestination: Destination,
  onSuccess: () => void
) {
  const seg = coordsStr.split(',')
  const lat = Number.parseFloat(seg[0]?.trim() ?? '')
  const lon = Number.parseFloat(seg[1]?.trim() ?? '')
  if (coordsStr.trim() === '' || !Number.isFinite(lat) || !Number.isFinite(lon)) {
    toast.add({ title: t('errors.ubicacion.error') })
    return
  }
  destinationDisplayMode.value = 'gps_near_me'
  destinoActualLocation.value = coordsStr
  const resolved = await selectedDestStore.resolveDestinationByCoordinates(lat, lon, coordsStr)
  const fallback = {
    ...fallbackDestination,
    location: coordsStr,
    name_translated: '',
    label: ''
  } as Destination
  selectedDestination.value = resolved ?? removeCookieFileds(fallback)
  onSuccess()
}

function onSelect(item: CommandPaletteItem | undefined) {
  if (!item) return
  const destination = { ...(item as Destination & { fromSearchHistory?: boolean }) }
  if (destination.fromSearchHistory) {
    trackSearchLocationHistoryClicked({
      destination_id: destination.element_id ?? destination.id,
      destination_name: destination.name_translated,
      category_id: destination.category_id,
      destination_type: destination.type,
      country_id: destination.country_id,
      locale: locale.value,
      is_mobile: isMobile,
      surface: 'modal',
    })
    delete destination.fromSearchHistory
  }
  if (isCurrentGpsMenuPick(destination)) {
    selectedDestStore.getLocation((res: { err?: string; data?: string }) => {
      if (res.err) {
        toast.add({ title: res.err })
        searchTerm.value = ''
      } else {
        void finishCurrentLocationSelection(res.data ?? '', destination, () => {
          if (route.path.includes('search')) saveLocationSearch(selectedDestination.value)
          openDest.value = false
          openCalendar.value = true
        })
      }
    })
  } else {
    destinationDisplayMode.value = 'named'
    selectedDestination.value = destination
    if (route.path.includes('search')) saveLocationSearch(destination)
    openDest.value = false
    openCalendar.value = true
  }
}

function finishDestinationInputSelection() {
  editingDestInput.value = false
  openDestInput.value = false
  syncLabelDestinoInput()
}

function onSelectInput(item: InputMenuItem | null) {
  if (item == null || typeof item !== 'object') return
  if ('type' in item && (item.type === 'separator' || item.type === 'label')) return
  if (item.id === 'loading') return
  const destination = { ...(item as Destination & { fromSearchHistory?: boolean }) }
  if (destination.fromSearchHistory) {
    trackSearchLocationHistoryClicked({
      destination_id: destination.element_id ?? destination.id,
      destination_name: destination.name_translated,
      category_id: destination.category_id,
      destination_type: destination.type,
      country_id: destination.country_id,
      locale: locale.value,
      is_mobile: isMobile,
      surface: 'input_menu',
    })
    delete destination.fromSearchHistory
  }
  if (isCurrentGpsMenuPick(destination)) {
    selectedDestStore.getLocation((res: { err?: string; data?: string }) => {
      if (res.err) {
        toast.add({ title: res.err })
        editingDestInput.value = false
        openDestInput.value = false
        syncLabelDestinoInput()
      } else {
        void finishCurrentLocationSelection(res.data ?? '', destination, () => {
          if (route.path.includes('search')) saveLocationSearch(selectedDestination.value)
          finishDestinationInputSelection()
        })
      }
    })
    return
  }
  destinationDisplayMode.value = 'named'
  selectedDestination.value = destination
  if (route.path.includes('search')) saveLocationSearch(destination)
  finishDestinationInputSelection()
}

function clearInput() {
  if (selectedDestination.value) {
    if (labelDestination.value || labelDestino.value || searchTerm.value.trim()) {
      editingDestInput.value = true
      labelDestino.value = undefined
      searchTerm.value = ''
      nextTick(() => {
        searchTerm.value = ''
        const input = destHeaderInputMenu.value?.inputRef
        if (input) input.value = ''
      })
      return true
    }
  }
  return false
}

function isInputMenuLabel(item: unknown): item is { type: 'label'; label: string } {
  return item != null && typeof item === 'object' && 'type' in item && (item as { type?: string }).type === 'label'
}

function isDestinationMenuItem(item: unknown): item is Destination {
  if (item == null || typeof item !== 'object' || Array.isArray(item)) return false
  if (isInputMenuLabel(item)) return false
  const rowType = 'type' in item ? (item as { type?: string }).type : undefined
  if (rowType === 'separator') return false
  if ((item as { id?: unknown }).id === 'loading') return false
  return 'name_translated' in item
}

// Search
const isOpenSearch = ref(false)
const disableSearch = ref(false)
const buscar = async () => {
  if (!objIsEmpty(selectedDestination.value ?? {}) && labelDestino.value) {
    const searchDestination = selectedDestination.value ?? cookieDestination.value
    isOpenSearch.value = false
    const regexHotel = /(?<=\/[a-z]{2}\/)[^/]+(?=\.html)/
    const matchHotel = searchDestination?.url?.match(regexHotel)
    const slug = matchHotel ? matchHotel[0] : ''
    const hotelPath = `/${locale.value}/${slug}.html`
    const path = searchDestination?.category_id == 1 ? hotelPath : `/${locale.value}/search` // si es hotel, va a landing hotel
    disableSearch.value = true
    saveLocationSearch(searchDestination)
    await navigateTo({
      path,
      query: {
        'data-destinationId': searchDestination?.category_id === 3 ? '' : searchDestination?.element_id,
        'data-distance': searchDestination?.search_distance,
        'data-search': searchDestination?.name_translated,
        'data-country': searchDestination?.country_name_translated,
        'search': searchDestination?.name_translated,
        'data-checkinDate': selectedDay.value ? formatDateGuion(selectedDay.value) : '',
        'GFakeDate': selectedDay.value ? formatFakeDate(selectedDay.value) : '',
        'data-dateRangeMode': dateRangeMode.value || undefined,
        'curr': currency.value
      }
    })
  } else {
    openDest.value = true
  }
}

// Device
const deviceStore = useDeviceStore()
const isMobile = deviceStore.isMobile

// Border round
const appConfig = useAppConfig()
const app_round = appConfig.ui.button?.slots?.base ?? 'rounded-full'

// Props
const props = defineProps({
  isHero: {
    type: Boolean,
    default: false
  },
  enableModals: {
    type: Boolean,
    default: true
  },
  /** Hero tiene dos instancias (desktop/móvil); solo una debe montar los modales. */
  heroLayout: {
    type: String as PropType<'desktop' | 'mobile'>,
    default: undefined
  }
})

const { isHome } = useIsHome()
const { isScrolledInput } = storeToRefs(useScrollStore())

const shouldRenderModals = computed(() => {
  if (!props.enableModals) return false
  if (props.isHero) {
    if (!isHome.value || isScrolledInput.value) return false
    if (props.heroLayout === 'desktop') return !isMobile
    if (props.heroLayout === 'mobile') return isMobile
    return false
  }
  return !isHome.value || isScrolledInput.value
})

</script>

<template>

  <!-- Ubicacion -->
  <UModal v-if="shouldRenderModals" v-model:open="openDest" :title="t('labels.ubicacion')"
    :description="t('labels.ubicacion')" :ui="{
      overlay: 'bg-elevated/50',
      content: 'max-h-[min(32rem,calc(100dvh-8rem))] flex flex-col overflow-hidden',
    }">
    <template #content>
      <div class="flex max-h-[min(32rem,calc(100dvh-8rem))] min-h-0 flex-1 flex-col overflow-hidden">
        <UCommandPalette close :autofocus="false" class="min-h-0 flex-1" @update:model-value="onSelect" v-model:search-term="searchTerm"
          @update:open="openDest = $event"
          :groups="commandPaletteGroups"
          preserve-group-order
          :loading="pendingDest === 'pending'" icon="i-lucide-map-pin" :placeholder="t('labels.placeholderDestino')"
          :ui="{
            root: 'min-h-0 flex-1',
            content: 'relative flex min-h-0 flex-1 flex-col overflow-hidden',
            viewport: 'min-h-0 flex-1 overflow-y-auto overscroll-contain',
          }">
        <template #empty>
          <div v-if="pendingDest === 'pending'" class="flex min-h-32 flex-1 items-center justify-center py-8">
            <UIcon name="i-lucide:loader-circle" class="animate-spin text-lg mr-2" />
            <span>{{ $t('buttons.cargando') }}</span>
          </div>
          <UEmpty v-else :title="$t('labels.errorResultadoTitle')" :description="$t('labels.errorResultado')"
            icon="i-lucide-search" variant="naked" :actions="[{
              label: $t('labels.ubicacionActual'),
              icon: 'i-ic-round-near-me',
              color: 'neutral',
              variant: 'solid',
              onClick: () => onSelect(destinoActual[0])
            }]" />
        </template>
        </UCommandPalette>
      </div>
    </template>
  </UModal>

  <!-- Calendario -->
  <UModal v-if="shouldRenderModals" v-model:open="openCalendar"
    :ui="{
      overlay: 'bg-elevated/50',
      content: 'sm:w-fit max-h-[min(32rem,calc(100dvh-8rem))] flex flex-col overflow-hidden ring-0 border border-neutral-200 rounded-lg',
    }"
    title="Calendario" description="Calendario">
    <template #content>
      <div class="flex max-h-[min(32rem,calc(100dvh-8rem))] min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-4">
        <UCalendar v-model="calendarDate" class="search-calendar shrink-0" :min-value="minCalendarDate" color="neutral"
          size="lg" :year-controls="false"
          @update:model-value="() => { openCalendar = false; openCalendarPopover = false }">
          <template #heading="{ value }">
            {{ value.charAt(0).toUpperCase() + value.slice(1) }}
          </template>
          <template #day="{ day }">
            <span
              class="inline-flex items-center justify-center"
              :data-range-adjacent="isRangeDayMarked(day) ? '' : undefined">
              <UChip :show="isDateMarked(day, hotelDispo)" color="success" size="2xs">
                {{ day.day }}
              </UChip>
            </span>
          </template>
        </UCalendar>
        <div class="flex shrink-0 flex-col gap-3 mt-4">
          <div class="flex justify-center gap-1 w-full" role="radiogroup" :aria-label="$t('labels.dateRangeSelector')">
            <UButton v-for="opt in dateRangeModeOptions" :key="opt.value" size="xs" block color="neutral"
              :variant="dateRangeMode === opt.value ? 'solid' : 'soft'" :disabled="opt.disabled"
              :aria-label="opt.ariaLabel" @click="selectDateRangeMode(opt.value)">
              {{ opt.label }}
            </UButton>
          </div>
          <div class="flex justify-center gap-2">
            <UButton size="lg" :aria-label="$t('labels.hoy')" block color="neutral" variant="outline"
              @click="selectHoy()">{{
                $t('labels.hoy') }}</UButton>
            <UButton size="lg" :aria-label="$t('labels.mañana')" block color="neutral" variant="outline"
              @click="selectMañana()">{{ $t('labels.mañana') }}</UButton>
          </div>
        </div>
      </div>
    </template>
  </UModal>

  <!-- Hero Search Input -->
  <div v-if="props.isHero" :class="{ 'opacity-90 transition-opacity pointer-events-none': showHeaderSkeleton }">
    <div :class="`relative h-full`">
      <div
        class="w-full flex flex-col md:flex-row gap-2 md:bg-white p-1 rounded-full items-center md:shadow-none md:hover:shadow-md transition-shadow duration-300 ease-in-out">

        <!-- Ubicacion -->
        <UButton @click="openDest = true; mobileButton = false"
          :label="selectedDestination ? labelDestinationDisplay : t('labels.ubicacion')" color="neutral" variant="link"
          icon="i-lucide-map-pin" size="xl" :ui="{
            base: 'w-full md:min-w-72 md:max-w-72 bg-white'
          }" />

        <USeparator class="hidden md:block h-6 w-0.5 bg-neutral-200" />

        <!-- Calendario -->
        <UButton @click="() => { openCalendar = true }" :aria-label="calendarDateAriaLabel" color="neutral" variant="link"
          icon="i-lucide-calendar" size="xl" :ui="{
            base: 'w-full min-w-0 flex-nowrap whitespace-nowrap md:pr-10 md:min-w-56 md:max-w-72 bg-white',
          }">
          <span
            class="flex min-w-0 max-w-full items-center gap-1 overflow-hidden whitespace-nowrap text-left leading-none">
            <span class="min-w-0 truncate">{{ calendarDateMainLabel }}</span>
            <span v-if="dateRangeModeShortLabel" class="shrink-0 text-xs text-dimmed whitespace-nowrap">
              {{ dateRangeModeShortLabel }}
            </span>
          </span>
        </UButton>

        <!-- Search -->
        <UButton :disabled="disableSearch" class="bg-linear-to-r from-primary-400 to-pink-600" block
          :aria-label="$t('buttons.buscar')" @click="buscar()" size="xl" :label="$t('buttons.buscar')">
          <template #leading>
            <UIcon v-if="!disableSearch" class="text-xl min-w-5" name="i-lucide-search" />
            <svg v-else class="animate-spin h-5 min-w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
          </template>
        </UButton>
      </div>
    </div>
  </div>

  <template v-else>
    <!-- Header Input md (Desktop) -->
    <div
      :class="`w-full sm:w-[450px] hidden sm:flex ${showHeaderSkeleton ? 'opacity-80 transition-opacity pointer-events-none' : ''}`">
      <!-- Input ubicación, calendario y buscar -->
      <div
        :class="`truncate w-full flex gap-2 border border-neutral-200 bg-white ${app_round} items-center p-0.5 shadow-none hover:shadow-sm transition-shadow duration-300 ease-in-out justify-between`">
        <!-- Ubicacion -->
        <UInputMenu ref="destHeaderInputMenu" v-model="labelDestino" v-model:search-term="searchTerm"
          v-model:open="openDestInput" open-on-click open-on-focus :reset-search-term-on-blur="false"
          @update:model-value="onSelectInput" :items="itemsInput"
          :loading="isMounted && pendingDest === 'pending' && isDestinationAutocompleteSearch"
          icon="i-lucide-map-pin" :placeholder="t('labels.ubicacion')" class="truncate" ignore-filter size="md"
          trailing-icon="" variant="none" :ui="{
            base: 'w-62 text-toned truncate hover:text-default',
            content: 'mt-2 max-h-[min(25rem,calc(var(--reka-combobox-content-available-height)-1rem))] w-fit min-w-96',
            leadingIcon: 'text-toned',
          }" :content="{
            align: 'start',
            hideWhenEmpty: false,
          }" @focus="clearInput">
          <template #item-label="{ item }">
            <template v-if="isDestinationMenuItem(item)">
              {{ item.name_translated || item.label }}
              <span v-if="item.destination_name" class="text-dimmed">
                {{ item.destination_name }},
              </span>
              <span class="text-dimmed">
                {{ item.country_name_translated }}
              </span>
            </template>
          </template>
          <template #empty>
            <div v-if="isDestinationAutocompleteSearch && pendingDest === 'pending'"
              class="min-h-72 h-full flex justify-center items-center">
              <UIcon name="i-lucide:loader-circle" class="animate-spin text-lg mr-2" />
              <span>{{ $t('buttons.cargando') }}</span>
            </div>
            <UEmpty v-else-if="isDestinationAutocompleteSearch" :title="$t('labels.errorResultadoTitle')"
              :description="$t('labels.errorResultado')" icon="i-lucide-search" variant="naked" size="sm" :actions="[{
                label: $t('labels.ubicacionActual'),
                icon: 'i-ic-round-near-me',
                color: 'neutral',
                variant: 'solid',
                size: 'xs',
                onClick: () => { const d = destinoActual[0]; if (d) onSelectInput(d) }
              }]" />
          </template>
        </UInputMenu>

        <USeparator class="hidden md:block h-6 w-0.5 bg-neutral-200" />

        <!-- Calendario -->
        <UPopover v-model:open="openCalendarPopover" :ui="{
          content: 'ring-0 border border-neutral-200 rounded-lg max-h-[min(32rem,calc(var(--reka-popover-content-available-height)-1rem))] overflow-y-auto overscroll-contain',
        }">
          <UButton :aria-label="calendarDateAriaLabel" color="neutral" variant="link" icon="i-lucide-calendar" size="md">
            <span
              class="flex min-w-0 max-w-full items-center gap-1 overflow-hidden whitespace-nowrap text-left leading-none">
              <span class="min-w-0 truncate">{{ calendarDateMainLabel }}</span>
              <span v-if="dateRangeModeShortLabel" class="shrink-0 text-xs text-dimmed whitespace-nowrap">
                {{ dateRangeModeShortLabel }}
              </span>
            </span>
          </UButton>

          <template #content>
            <div class="p-4">
              <UCalendar v-model="calendarDate" class="search-calendar" :min-value="minCalendarDate" color="neutral"
                size="md" :year-controls="false"
                @update:model-value="() => { openCalendar = false; openCalendarPopover = false }">
                <template #heading="{ value }">
                  {{ value.charAt(0).toUpperCase() + value.slice(1) }}
                </template>
                <template #day="{ day }">
                  <span
                    class="inline-flex items-center justify-center"
                    :data-range-adjacent="isRangeDayMarked(day) ? '' : undefined">
                    <UChip :show="isDateMarked(day, hotelDispo)" color="success" size="2xs">
                      {{ day.day }}
                    </UChip>
                  </span>
                </template>
              </UCalendar>
              <div class="flex flex-col gap-3 mt-4">
                <div class="flex justify-center gap-1 w-full" role="radiogroup"
                  :aria-label="$t('labels.dateRangeSelector')">
                  <UButton v-for="opt in dateRangeModeOptions" :key="opt.value" size="xs" block color="neutral"
                    :variant="dateRangeMode === opt.value ? 'solid' : 'soft'" :disabled="opt.disabled"
                    :aria-label="opt.ariaLabel" @click="selectDateRangeMode(opt.value)">
                    {{ opt.label }}
                  </UButton>
                </div>
                <div class="flex justify-center gap-2">
                  <UButton size="sm" :aria-label="$t('labels.hoy')" block color="neutral" variant="outline"
                    @click="selectHoy()">{{
                      $t('labels.hoy') }}</UButton>
                  <UButton size="sm" :aria-label="$t('labels.mañana')" block color="neutral" variant="outline"
                    @click="selectMañana()">{{ $t('labels.mañana') }}</UButton>
                </div>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- Search -->
        <div>
          <UButton class="bg-byhours-gradient" :aria-label="$t('buttons.buscar')"
            @click="buscar()" icon="i-lucide-search" size="lg" />
        </div>
      </div>
    </div>

    <!-- Header Button (Mobile) -->
    <div
      :class="`sm:hidden w-full max-w-full min-w-0 overflow-hidden sm:w-[500px] ${showHeaderSkeleton ? 'opacity-80 transition-opacity pointer-events-none' : ''}`">
      <!-- Botones destino y fecha -->
      <div class="table table-fixed w-full max-w-full">
        <div class="table-cell w-1/2 align-middle overflow-hidden">
          <UButton size="sm" :ui="{ base: 'rounded-full rounded-r-none justify-center w-full' }"
            @click="openDest = true; mobileButton = true" color="neutral" variant="outline">
            <span class="block min-w-0 truncate text-left">{{ labelDestinationDisplay || '🗺️' }}</span>
          </UButton>
        </div>
        <div class="table-cell w-1/2 align-middle overflow-hidden">
          <UButton size="sm" :ui="{ base: 'rounded-full rounded-l-none justify-center w-full min-w-0 flex-nowrap whitespace-nowrap' }"
            @click="openCalendar = true;" color="neutral" variant="outline" :aria-label="calendarDateAriaLabel">
            <span
              class="flex min-w-0 max-w-full items-center justify-center gap-0.5 p-0.5 overflow-hidden whitespace-nowrap leading-none">
              <span class="min-w-0 truncate">{{ calendarDateMainLabel }}</span>
              <span v-if="dateRangeModeShortLabel" class="shrink-0 text-[10px] text-dimmed whitespace-nowrap">
                {{ dateRangeModeShortLabel }}
              </span>
            </span>
          </UButton>
        </div>
      </div>
    </div>
  </template>

</template>

<style scoped>
.search-calendar :deep([data-slot="cellTrigger"]:has([data-range-adjacent])) {
  border: 1px dashed var(--ui-color-neutral-500, #737373);
}
</style>
